import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, LocationOn, ContentPaste, Clear } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { GoogleMap } from '@react-google-maps/api';
import apiService from '../services/apiService';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

const mapContainerStyle = {
  width: '100%',
  height: '400px',
};

const defaultCenter = {
  lat: 12.9716,
  lng: 77.5946,
};

const RegionsPage = () => {
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [polygonPath, setPolygonPath] = useState([]);
  const [drawingManager, setDrawingManager] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [showJsonPaste, setShowJsonPaste] = useState(false);
  const nativePolygonRef = useRef(null);

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    // Load Google Maps dynamically
    loadGoogleMaps()
      .then(() => {
        console.log('Google Maps API loaded successfully');
        console.log('window.google.maps.drawing available:', !!window.google?.maps?.drawing);
        setIsLoaded(true);
      })
      .catch((error) => {
        console.error('Error loading Google Maps:', error);
        setLoadError(error);
      });
  }, []);

  // Fit map bounds to polygon when editing or when JSON is pasted
  useEffect(() => {
    if (mapInstance && polygonPath.length > 0 && openDialog) {
      const bounds = new window.google.maps.LatLngBounds();
      polygonPath.forEach(point => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });
      mapInstance.fitBounds(bounds);
    }
  }, [mapInstance, polygonPath, openDialog]);

  // Draw/update native Google Maps polygon when polygonPath changes
  useEffect(() => {
    // Remove previous polygon
    if (nativePolygonRef.current) {
      nativePolygonRef.current.setMap(null);
      nativePolygonRef.current = null;
    }

    // Draw new polygon if we have a map and path
    if (mapInstance && polygonPath.length > 0 && window.google) {
      const polygon = new window.google.maps.Polygon({
        paths: polygonPath.map(p => ({ lat: p.lat, lng: p.lng })),
        fillColor: '#FFC107',
        fillOpacity: 0.3,
        strokeWeight: 2,
        strokeColor: '#FFC107',
        clickable: false,
      });
      polygon.setMap(mapInstance);
      nativePolygonRef.current = polygon;
    }
  }, [mapInstance, polygonPath]);

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const response = await apiService.getRegions();

      if (response.success) {
        setRegions(response.data);
        setError('');
      } else {
        setError(response.message || 'Failed to load regions');
        setRegions([]);
      }
    } catch (err) {
      console.error('Regions error:', err);
      setError('Failed to load regions. Please check your connection and try again.');
      setRegions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedRegion(null);
    setPolygonPath([]);
    setJsonInput('');
    setJsonError('');
    setShowJsonPaste(false);
    if (drawingManager) {
      drawingManager.setMap(null);
    }
    setDrawingManager(null);
    setMapInstance(null);
    setOpenDialog(true);
  };


  // Parse pasted JSON and apply region coordinates + name to the form
  const handleJsonPaste = (setFieldValue) => {
    setJsonError('');

    if (!jsonInput.trim()) {
      setJsonError('Please paste the region JSON data');
      return;
    }

    try {
      let parsed = JSON.parse(jsonInput.trim());

      // Support both array format and single object format
      // Array format: [{ name, coordinates, area_sqm }]
      // Single object: { name, coordinates, area_sqm }
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
          setJsonError('JSON array is empty. Please paste valid region data.');
          return;
        }
        parsed = parsed[0]; // Take the first region
      }

      // Extract coordinates
      const coordinates = parsed.coordinates || parsed.boundary_polygon;
      if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 3) {
        setJsonError('Invalid JSON: must contain "coordinates" array with at least 3 points. Expected format: [{ "lat": 12.97, "lng": 77.59 }, ...]');
        return;
      }

      // Validate each coordinate has lat and lng
      for (let i = 0; i < coordinates.length; i++) {
        const point = coordinates[i];
        if (typeof point.lat !== 'number' || typeof point.lng !== 'number') {
          setJsonError(`Invalid coordinate at index ${i}: each point must have numeric "lat" and "lng" fields`);
          return;
        }
      }

      // Apply polygon path
      const path = coordinates.map(p => ({ lat: p.lat, lng: p.lng }));
      setPolygonPath(path);

      // Auto-fill region name if present and form name is empty
      if (parsed.name && setFieldValue) {
        setFieldValue('name', parsed.name);
      }

      // Fit map to the new polygon bounds
      if (mapInstance) {
        const bounds = new window.google.maps.LatLngBounds();
        path.forEach(point => {
          bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
        });
        mapInstance.fitBounds(bounds);
      }

      // Clear JSON input and hide the paste area
      setJsonInput('');
      setJsonError('');
      setShowJsonPaste(false);
    } catch (e) {
      setJsonError(`Invalid JSON format: ${e.message}`);
    }
  };

  const handleEdit = (region) => {
    setEditMode(true);
    setSelectedRegion(region);
    setJsonInput('');
    setJsonError('');
    setShowJsonPaste(false);
    if (drawingManager) {
      drawingManager.setMap(null);
    }
    setDrawingManager(null);
    setMapInstance(null);

    // Parse boundary polygon if exists
    if (region.boundary_polygon) {
      try {
        const parsedPath = JSON.parse(region.boundary_polygon);
        setPolygonPath(parsedPath);
      } catch (e) {
        console.error('Error parsing polygon:', e);
        setPolygonPath([]);
      }
    } else {
      setPolygonPath([]);
    }

    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this region?')) {
      return;
    }

    try {
      const response = await apiService.deleteRegion(id);

      if (response.success) {
        alert('Region deleted successfully');
        fetchRegions();
      } else {
        alert(response.message || 'Failed to delete region');
      }
    } catch (err) {
      alert('Failed to delete region');
    }
  };


  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Validate that boundary polygon is drawn
      if (polygonPath.length === 0) {
        alert('Please draw a region boundary on the map. This is required.');
        setSubmitting(false);
        return;
      }

      // Add boundary polygon
      const submitData = {
        ...values,
        boundary_polygon: JSON.stringify(polygonPath),
      };

      let response;
      if (editMode) {
        response = await apiService.updateRegion(selectedRegion.id, submitData);
      } else {
        response = await apiService.addRegion(submitData);
      }

      if (response.success) {
        alert(`Region ${editMode ? 'updated' : 'created'} successfully`);
        setOpenDialog(false);
        setPolygonPath([]);
        fetchRegions();
      } else {
        alert(response.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Region name is required'),
    state: Yup.string().required('State is required'),
    country: Yup.string().required('Country is required'),
    distance_unit: Yup.string().required('Distance unit is required'),
    status: Yup.string().required('Status is required'),
  });

  const columns = [
    {
      field: 'name',
      headerName: 'Region Name',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationOn color="primary" />
          <Typography variant="body2" fontWeight={600}>
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'state',
      headerName: 'State',
      width: 150,
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 120,
    },
    {
      field: 'distance_unit',
      headerName: 'Distance Unit',
      width: 130,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) =>
        params.row.status === 'active' ? (
          <Chip label="Active" color="success" size="small" />
        ) : (
          <Chip label="Inactive" color="default" size="small" />
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Regions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage service regions with geographic boundaries
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Region
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <DataGrid
            rows={regions}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Region Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          if (drawingManager) {
            drawingManager.setMap(null);
          }
          if (nativePolygonRef.current) {
            nativePolygonRef.current.setMap(null);
            nativePolygonRef.current = null;
          }
          setOpenDialog(false);
          setDrawingManager(null);
          setMapInstance(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Region</DialogTitle>
        <Formik
          initialValues={{
            name: selectedRegion?.name || '',
            state: selectedRegion?.state || '',
            country: selectedRegion?.country || 'India',
            distance_unit: selectedRegion?.distance_unit || 'km',
            status: selectedRegion?.status || 'active',
            description: selectedRegion?.description || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Region Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      placeholder="e.g., Bangalore Central"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="state"
                      label="State"
                      value={values.state}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.state && Boolean(errors.state)}
                      helperText={touched.state && errors.state}
                      placeholder="e.g., Karnataka"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="country"
                      label="Country"
                      value={values.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.country && Boolean(errors.country)}
                      helperText={touched.country && errors.country}
                      placeholder="e.g., India"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Distance Unit</InputLabel>
                      <Select
                        name="distance_unit"
                        value={values.distance_unit}
                        onChange={handleChange}
                        label="Distance Unit"
                        error={touched.distance_unit && Boolean(errors.distance_unit)}
                      >
                        <MenuItem value="km">Kilometer (km)</MenuItem>
                        <MenuItem value="mile">Mile</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="description"
                      label="Description"
                      multiline
                      rows={2}
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Optional description of the region"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">
                        Region Boundary <span style={{ color: 'red' }}>*</span>
                      </Typography>
                      <Button
                        size="small"
                        variant={showJsonPaste ? 'contained' : 'outlined'}
                        startIcon={<ContentPaste />}
                        onClick={() => {
                          setShowJsonPaste(!showJsonPaste);
                          setJsonError('');
                        }}
                      >
                        {showJsonPaste ? 'Hide JSON Paste' : 'Paste Region JSON'}
                      </Button>
                    </Box>

                    {/* JSON Paste Section */}
                    {showJsonPaste && (
                      <Box sx={{ mb: 2, p: 2, border: '1px solid #1976d2', borderRadius: 1, bgcolor: '#f5f9ff' }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                          Paste Client's Region JSON Data
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                          Paste the JSON from the client's region drawing tool. Supports formats like:<br/>
                          <code>{`[{ "name": "Region 1", "coordinates": [{ "lat": 12.97, "lng": 77.59 }, ...] }]`}</code>
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={5}
                          value={jsonInput}
                          onChange={(e) => {
                            setJsonInput(e.target.value);
                            setJsonError('');
                          }}
                          placeholder={`Paste region JSON here, e.g.:\n[\n  {\n    "name": "Region 1",\n    "coordinates": [\n      { "lat": 12.769092, "lng": 80.222975 },\n      { "lat": 12.868957, "lng": 80.105482 }\n    ]\n  }\n]`}
                          sx={{ mb: 1.5, bgcolor: 'white', fontFamily: 'monospace', fontSize: '0.85rem' }}
                          InputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                        />
                        {jsonError && (
                          <Alert severity="error" sx={{ mb: 1.5 }}>
                            {jsonError}
                          </Alert>
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<ContentPaste />}
                            onClick={() => handleJsonPaste(setFieldValue)}
                          >
                            Apply Region Data
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Clear />}
                            onClick={() => {
                              setJsonInput('');
                              setJsonError('');
                            }}
                          >
                            Clear
                          </Button>
                        </Box>
                      </Box>
                    )}

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>How to set region boundary:</strong>
                      </Typography>
                      <Typography variant="caption" component="div">
                        <strong>Option 1 - Paste JSON:</strong> Click "Paste Region JSON" above and paste client's region data<br/>
                        <strong>Option 2 - Draw manually:</strong> Use the drawing tools at the top center of the map, click the polygon tool, click points on the map, then click the first point to close
                      </Typography>
                    </Alert>

                    {loadError && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        Error loading Google Maps. Please check your API key and internet connection.
                      </Alert>
                    )}

                    {!isLoaded && !loadError && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Loading Google Maps...
                      </Alert>
                    )}

                    {isLoaded && drawingManager && (
                      <Alert severity="success" sx={{ mb: 1 }}>
                        ✓ Map ready! Use the drawing tools at the top center of the map to draw the region boundary.
                      </Alert>
                    )}

                    {isLoaded && (
                      <Box
                        sx={{
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative'
                        }}
                      >
                        <GoogleMap
                          key={openDialog ? 'map-open' : 'map-closed'}
                          mapContainerStyle={mapContainerStyle}
                          center={defaultCenter}
                          zoom={12}
                          onLoad={(map) => {
                            console.log('Map loaded successfully');
                            console.log('Google Maps Drawing available:', !!window.google?.maps?.drawing);

                            // Store map instance
                            setMapInstance(map);

                            // Manually create DrawingManager to ensure controls appear
                            if (window.google && window.google.maps && window.google.maps.drawing) {
                              const dm = new window.google.maps.drawing.DrawingManager({
                                drawingMode: null,
                                drawingControl: true,
                                drawingControlOptions: {
                                  position: window.google.maps.ControlPosition.TOP_CENTER,
                                  drawingModes: [window.google.maps.drawing.OverlayType.POLYGON],
                                },
                                polygonOptions: {
                                  fillColor: '#FFC107',
                                  fillOpacity: 0.3,
                                  strokeWeight: 2,
                                  strokeColor: '#FFC107',
                                  clickable: true,
                                  editable: true,
                                  draggable: false,
                                },
                              });

                              dm.setMap(map);
                              setDrawingManager(dm);

                              window.google.maps.event.addListener(dm, 'polygoncomplete', (polygon) => {
                                const path = polygon.getPath();
                                const coordinates = [];
                                for (let i = 0; i < path.getLength(); i++) {
                                  const point = path.getAt(i);
                                  coordinates.push({
                                    lat: point.lat(),
                                    lng: point.lng(),
                                  });
                                }
                                setPolygonPath(coordinates);
                                polygon.setMap(null);
                              });

                              console.log('✅ DrawingManager manually initialized with controls');
                            }
                          }}
                        >
                        </GoogleMap>
                      </Box>
                    )}

                    {polygonPath.length > 0 && (
                      <Box mt={1}>
                        <Chip
                          label={`Boundary drawn (${polygonPath.length} points)`}
                          color="success"
                          size="small"
                          onDelete={() => {
                              setPolygonPath([]);
                              }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {editMode ? 'Update' : 'Create'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default RegionsPage;
